import React, { useState, useEffect } from 'react';

const MindmapVisualizer = ({ nodes = [], edges = [], onNodeClick, onNodeHover, className = '' }) => {
  console.log('Rendering MindmapVisualizer with nodes:', nodes, 'and edges:', edges);
  
  
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  // Filter out phase nodes that have no children
  const filteredNodes = nodes.filter(node => {
    if (node.type === 'phase') {
      // Check if this phase node has any children
      const hasChildren = edges.some(edge => edge.source === node.id);
      return hasChildren;
    }
    return true;
  });

  const getNodeSize = (node) => {
    if (node.size === 'small') return 8;
    if (node.type === 'center') return 20;
    if (node.type === 'phase') return 16;
    if (node.type === 'ability') return 12;
    return 10;
  };

  const getNodeStroke = (node) => {
    if (node.type === 'center') return '#ffffff';
    if (node.type === 'phase') return '#ffffff';
    if (node.type === 'ability') return '#ffffff';
    return '#ffffff';
  };

  const handleMouseDown = (e, node) => {
    setDraggedNode(node.id);
    setDragOffset({
      x: e.clientX - node.x,
      y: e.clientY - node.y
    });
  };

  const handleMouseMove = (e) => {
    if (draggedNode) {
      e.preventDefault();
    }
  };

  const handleMouseUp = () => {
    setDraggedNode(null);
    setDragOffset({ x: 0, y: 0 });
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    if (onNodeClick) onNodeClick(node);
  };

  const handleNodeHover = (node) => {
    setHoveredNode(node);
    if (onNodeHover) onNodeHover(node);
  };

  const handleNodeLeave = () => {
    setHoveredNode(null);
  };

  const displayNode = hoveredNode || selectedNode;

  return (
    <div className={`w-full relative ${className} my-4`}>
      
      {displayNode && (
        <div className="absolute top-4 right-4 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-10 min-w-64">
          <div className="flex items-center gap-2 mb-1">
            <div 
              className="w-4 h-4 rounded-full" 
              style={{ backgroundColor: displayNode.color || '' }}
            ></div>
            <h3 className="font-semibold text-gray-800 capitalize">{displayNode.type || 'Node'}</h3>
          </div>
          
          {displayNode.label && (
            <div className="mb-4">
              <p className="text-sm mb-4 font-medium text-gray-700">{displayNode.label}</p>
            </div>
          )}
          
          {displayNode.description && (
            <div className="mb-2">
              <p className="text-xs text-gray-600">{displayNode.description}</p>
            </div>
          )}
          
          {selectedNode && (
            <button 
              onClick={() => setSelectedNode(null)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-sm"
            >
              ×
            </button>
          )}
        </div>
      )}

      <svg 
        width="100%" 
        height="60vh" 
        viewBox="0 0 800 600"
        className="border border-gray-200 rounded-lg bg-white"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Render edges first */}
        {edges.map(edge => {
          const sourceNode = filteredNodes.find(n => n.id === edge.source);
          const targetNode = filteredNodes.find(n => n.id === edge.target);
          
          if (!sourceNode || !targetNode) return null;
          
          return (
            <line
              key={edge.id}
              x1={sourceNode.x}
              y1={sourceNode.y}
              x2={targetNode.x}
              y2={targetNode.y}
              stroke="#D1D5DB"
              strokeWidth="2"
              opacity="0.6"
            />
          );
        })}
        
        {/* Render nodes */}
        {filteredNodes.map(node => (
          <circle
            key={node.id}
            cx={node.x}
            cy={node.y}
            r={getNodeSize(node)}
            fill={node.color || '#e3daeb'}
            stroke={getNodeStroke(node)}
            strokeWidth="2"
            className="cursor-pointer hover:opacity-80 transition-opacity"
            onMouseDown={(e) => handleMouseDown(e, node)}
            onClick={() => handleNodeClick(node)}
            onMouseEnter={() => handleNodeHover(node)}
            onMouseLeave={handleNodeLeave}
          />
        ))}
      </svg>
      
      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-4 text-sm">

        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#62BB67]"></div>
          <span>Skills</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#378FC0]"></div>
          <span>Knowledge</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#E4A204]"></div>
          <span>Mindset</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#7C1586]"></div>
          <span>Instruments</span>
        </div>
      </div>
    </div>
  );
};

export default MindmapVisualizer;